/**
 * Dual-language UI copy (Jacky, 2026-09-16):
 *
 *   "we need dual language support — Chinese UI with full translation required,
 *    and future build all require a Chinese UI and translation — same rule,
 *    product name, supplier name no translation."
 *
 * ONE MECHANISM, ONE DICTIONARY PER FACE. The installer portal already shipped
 * English + Simplified Chinese with its own tables; those stay where they are and
 * this module becomes the mechanism they run on. A second translation layer would
 * be a fork by another name.
 *
 *   · A screen asks for an **id**, never a literal, so a missing translation is a
 *     compile error (`Record<MessageId, string>`), not a page half in English.
 *   · `translate()` never throws in production: a missing id shows `⟦id⟧` — loud
 *     enough to be seen, quiet enough not to blank a page a customer is reading.
 *   · Interpolation is `{name}` substitution, so a translated sentence may order
 *     its words differently. Nothing builds a sentence by joining two messages.
 *
 * The standing rule this implements is board card `AIW-CN`.
 */
import {EN, type MessageId} from './en.js';
import {ZH} from './zh.js';

export const LANGS = ['en', 'zh'] as const;
export type Lang = (typeof LANGS)[number];

/** Each language names itself in its own script: "Chinese" written in English is
 *  no use to the person who needs to find the switch. */
export const LANG_LABEL: Record<Lang, string> = { en: 'English', zh: '中文（简体）' };
export const LANG_SHORT: Record<Lang, string> = { en: 'EN', zh: '中' };

export type Vars = Record<string, string | number>;

/** A complete dictionary: every id in the source language, translated. */
export type Dictionary<S extends string = string> = Record<S, string>;
export type Translation<D> = Record<keyof D, string>;

export const DICTIONARIES: Record<Lang, Dictionary<MessageId>> = { en: EN, zh: ZH };

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

/** What to show someone who has never chosen: their own browser's language.
 *  A Chinese-reading member should not have to find the switch first. */
export function preferredLang(navigatorLanguage?: string | null): Lang {
  return /^zh\b/i.test(navigatorLanguage ?? '') ? 'zh' : 'en';
}

/** `{name}` placeholders. An absent variable stays in braces rather than becoming
 *  the word "undefined" in front of a customer. */
export function format(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : whole);
}

/** Translate against ANY dictionary — the shared one here, or a face's own. */
export function translateIn<S extends string>(dict: Record<Lang, Dictionary<S>>, lang: Lang, id: S, vars?: Vars): string {
  const template = dict[lang]?.[id] ?? dict.en[id];
  if (template === undefined) return `⟦${id}⟧`;
  return format(template, vars);
}

export function translate(lang: Lang, id: MessageId, vars?: Vars): string {
  return translateIn(DICTIONARIES, lang, id, vars);
}

export type T = (id: MessageId, vars?: Vars) => string;

export function createT(lang: Lang): T {
  return (id, vars) => translate(lang, id, vars);
}

// ── the browser bits, shared so no face re-invents them ──────────────────────

/** The stored choice for one face, or the browser's preference. Each face keeps
 *  its own key because each is a separate origin; the CONCEPT is shared. */
export function storedLang(storageKey: string, navigatorLanguage?: string | null): Lang {
  try {
    const value = globalThis.localStorage?.getItem(storageKey);
    if (isLang(value)) return value;
  } catch { /* private mode, or no DOM */ }
  return preferredLang(navigatorLanguage ?? globalThis.navigator?.language ?? null);
}

export function documentLang(lang: Lang): void {
  try { const doc=(globalThis as typeof globalThis & {document?:{documentElement:{lang:string}}}).document; if (doc) doc.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en-AU'; } catch { /* no DOM */ }
}

/**
 * The engine's customer-facing notices arrive as English text — it is a shared
 * domain package and its words are data (ADR-004). The ones a customer reads are
 * mapped back onto ids; anything unmapped passes through in English rather than
 * being dropped or invented.
 *
 * Staff-facing engine *warnings* are deliberately not mapped in this cut: they
 * explain to staff why a line will not price, and inventing a Chinese wording for
 * a costing term is Jacky's call, not a lookup table's.
 */
const NOTICE_IDS: Record<string, MessageId> = {
  'Final price depends on your selected colour.': 'engine.notice.selectColour',
  'Promotional pricing — contact us to confirm availability.': 'engine.notice.promotion',
  // Raised by `projectCustomerPricing` itself when any line is a range, so it
  // arrives the same way an engine notice does.
  'One or more lines depend on a colour that is not chosen yet. The final price is confirmed when you choose.': 'priced.notice.undecidedColour',
  // The waste sentence on an area line is NOT matched here, on purpose: its percentage comes from the
  // `artemis_services` config, so a screen builds it with `wasteNoticeId()` + `wastePercent()` from
  // @artemis-group/aiw-contracts instead of matching English that moves when he changes a rate.
};

export function noticeText(t: T, notice: string): string {
  const id = NOTICE_IDS[notice];
  return id ? t(id) : notice;
}

/** The undecided colour as the contract states it in English, in the reader's
 *  language. `UNDECIDED_COLOUR` stays the single English source in the contract;
 *  this is its translation, not a second spelling of it. */
export function colourText(t: T, colour: string, undecided: string): string {
  return colour === undecided ? t('pricing.colour.undecided') : colour;
}

export {EN, ZH};
export type {MessageId};
